import React, { useState, useEffect, useRef } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import { FaEye, FaTrashAlt } from 'react-icons/fa';
import ButtonDefault from '../../../../components/ButtonDefault/index.js';
import InputDefault from '../../../../components/InputDefault/index.js';
import SelectDefault from '../../../../components/SelectDefault/index.js';
import GenericActionMenu from '../../../../components/GenericActionMenu/index.js';
import LoadingCard from './LoadingCard';
import { CardContainer, CardHeader, CardBody, TableContainer, FileUploadArea } from './CardStyles';
import api from '../../../../services/api.js';
import { toast } from 'react-toastify';

const FilesCard = ({ studyId, onDataUpdate, onModalOpen }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [filesList, setFilesList] = useState([]);
  const [modalPreviewFile, setModalPreviewFile] = useState(false);
  const [previewFileBase64, setPreviewFileBase64] = useState('');
  const [modalAddFile, setModalAddFile] = useState(false);
  const [filePreview, setFilePreview] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [documentType, setDocumentType] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchFilesData = async () => {
      if (!studyId) return;
      
      try {
        setIsLoading(true);
        const fileResponse = await api.get(`files?id=${studyId}`);
        const data = fileResponse.data.data;
        
        if (fileResponse.status === 200) {
          setFilesList(data);
          onDataUpdate(data);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error('Erro ao buscar arquivos:', error);
      }
    };

    fetchFilesData();
  }, [studyId, onDataUpdate]);

  const getDocumentTypeDescription = (type) => {
    const types = {
      1: 'CONTRATO SOCIAL',
      2: 'PROCURAÇÃO',
      3: 'RG',
      4: 'CPF',
      5: 'CONTA DE LUZ/FATURA',
      6: 'ATA DE NOMEAÇÃO',
      7: 'OUTROS'
    };
    return types[type] || 'DESCONHECIDO';
  };

  const handlePreviewFile = (base64, mimeType) => {
    if (mimeType === 'application/pdf') {
      setPreviewFileBase64(`data:application/pdf;base64,${base64}`);
    } else {
      setPreviewFileBase64(`data:image/png;base64,${base64}`);
    }
    setModalPreviewFile(true);
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const validMimeTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/bmp'];
      if (!validMimeTypes.includes(file.type)) {
        toast.error('Tipo de arquivo não suportado! Use PDF, PNG, JPG, JPEG ou BMP.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
        setFileSize((file.size / (1024 * 1024)).toFixed(2));
      };
      reader.readAsDataURL(file);

      setModalAddFile(true);
    }
  };

  const handleAddFile = () => {
    if (!documentType || !documentDescription) {
      toast.error('Informe o tipo e a descrição do documento!');
      return;
    }

    const formData = new FormData();
    formData.append('id', studyId);
    formData.append('IN_TIPO_DOCUMENTO', documentType);
    formData.append('DS_DOCUMENTO', documentDescription);
    formData.append('file', fileInputRef.current.files[0]);

    api.post('send_study_files', formData).then((response) => {
      if (response.status === 200) {
        toast.success('Arquivo adicionado com sucesso!');
        // Refresh files list
        const fetchFiles = async () => {
          const fileResponse = await api.get(`files?id=${studyId}`);
          if (fileResponse.status === 200) {
            setFilesList(fileResponse.data.data);
            onDataUpdate(fileResponse.data.data);
          }
        };
        fetchFiles();
      } else {
        toast.error('Erro ao adicionar arquivo!');
      }
    }).catch(() => {
      toast.error('Erro ao adicionar arquivo!');
    });

    setModalAddFile(false);
    setDocumentType('');
    setDocumentDescription('');
  };

  if (isLoading) {
    return <LoadingCard title="Carregando Arquivos..." />;
  }

  return (
    <CardContainer>
      <CardHeader>
        <h5>Arquivos Anexados</h5>
      </CardHeader>
      <CardBody>
        <TableContainer>
          <Table
            striped
            bordered
            hover
            variant="light">
            <thead>
              <tr>
                <th style={{ width: '5%', textAlign: 'right' }}>Código</th>
                <th style={{ width: '20%' }}>Nome do Arquivo</th>
                <th style={{ width: '15%' }}>Tipo de Documento</th>
                <th style={{ width: '5%', textAlign: 'center' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {(filesList || []).map((item) => (
                <tr key={item.ID_DM015}>
                  <td style={{ textAlign: 'right' }}>{item.ID_DM015}</td>
                  <td>
                    <span
                      style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
                      onClick={() => handlePreviewFile(item.base64, item.mimeType)}
                    >
                      {item.DS_DOCUMENTO}
                    </span>
                  </td>
                  <td>{getDocumentTypeDescription(item.IN_TIPO_DOCUMENTO)}</td>
                  <td>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      height: '45px',
                      position: 'relative'
                    }}>
                      <GenericActionMenu
                        actions={[
                          {
                            key: 'view',
                            label: 'Visualizar',
                            icon: FaEye,
                            onClick: () => handlePreviewFile(item.base64, item.mimeType)
                          },
                          { type: 'divider' },
                          {
                            key: 'delete',
                            label: 'Excluir',
                            icon: FaTrashAlt,
                            variant: 'danger',
                            onClick: () => {
                              onModalOpen('deleteFile', item.ID_DM015, item.DS_DOCUMENTO);
                            }
                          }
                        ]}
                        buttonLabel={`Ações para ${item.DS_DOCUMENTO}`}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
        <FileUploadArea>
          <ButtonDefault
            Width={'200px'}
            Margin={'0px 10px 20px 20px'}
            Text="Adicionar arquivo"
            onClick={handleButtonClick}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </FileUploadArea>
      </CardBody>

      {/* Modal de Preview do Arquivo */}
      <Modal
        show={modalPreviewFile}
        onHide={() => setModalPreviewFile(false)}
        size="lg"
        centered
        style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <Modal.Header closeButton>
          <Modal.Title>Preview do Arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ textAlign: 'center' }}>
            {previewFileBase64.includes('application/pdf') ? (
              <iframe
                src={previewFileBase64}
                title="PDF Preview"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            ) : (
              <img
                src={previewFileBase64}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <ButtonDefault
            Width={'150px'}
            Text="Baixar Arquivo"
            onClick={() => {
              const link = document.createElement('a');
              link.href = previewFileBase64;
              link.download = 'arquivo';
              link.click();
            }}
          />
        </Modal.Footer>
      </Modal>

      {/* Modal de Adicionar Arquivo */}
      <Modal
        show={modalAddFile}
        onHide={() => setModalAddFile(false)}
        size="lg"
        centered>
        <Modal.Header closeButton>
          <Modal.Title>Adicionar Arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#f1f1f1' }}>
          <Row>
            <Col xs={12} md={5}>
              <InputDefault
                placeholder="Descrição do Documento"
                Margin={'15px 15px 15px 0px'}
                value={documentDescription}
                onChange={(e) => setDocumentDescription(e.target.value)}
                Text={'Descrição'}
              />
            </Col>
            <Col xs={12} md={6}>
              <SelectDefault
                Width={'100%'}
                Margin={'15px 0px 15px 0px'}
                Text={'Tipo de Documento'}
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}>
                <option value="">Selecione</option>
                <option value="1">CONTRATO SOCIAL</option>
                <option value="2">PROCURAÇÃO</option>
                <option value="3">RG</option>
                <option value="4">CPF</option>
                <option value="5">CONTA DE LUZ/FATURA</option>
                <option value="6">ATA DE NOMEAÇÃO</option>
                <option value="7">OUTROS</option>
              </SelectDefault>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <p style={{ textAlign: 'center' }}>Preview do Arquivo:</p>
              {filePreview.includes('application/pdf') ? (
                <iframe
                  src={filePreview}
                  title="PDF Preview"
                  style={{ width: '100%', height: '300px', border: 'none' }}
                />
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={filePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '300px' }}
                  />
                </div>
              )}
              <p style={{ textAlign: 'center' }}>Tamanho do Arquivo: {fileSize} MB</p>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <ButtonDefault
            Width={'150px'}
            Text="Adicionar"
            onClick={handleAddFile}
          />
          <ButtonDefault
            Width={'150px'}
            Text="Cancelar"
            onClick={() => setModalAddFile(false)}
          />
        </Modal.Footer>
      </Modal>
    </CardContainer>
  );
};

export default FilesCard;
